import { useEffect, useState } from 'react'
import './DailyReportModal.css'

interface DailyStats {
    goldEarned: number
    xpGained: number
    monstersDefeated: number
    gemsSpent: number
    euroSpent: number
}

interface DailyReportModalProps {
    isOpen: boolean
    data: DailyStats
    dayNumber: number
    debt: number
    onClose: () => void
}

export function DailyReportModal({ isOpen, data, dayNumber, debt, onClose }: DailyReportModalProps) {
    const [visibleStats, setVisibleStats] = useState(0)

    useEffect(() => {
        if (!isOpen) {
            setVisibleStats(0)
            return
        }

        // Sequential stat reveal animation
        const timeouts: NodeJS.Timeout[] = []
        for (let i = 1; i <= 7; i++) {
            timeouts.push(setTimeout(() => setVisibleStats(i), i * 300))
        }

        return () => timeouts.forEach(t => clearTimeout(t))
    }, [isOpen])

    if (!isOpen) return null

    // Debug: log data to see what's being passed
    console.log('Daily Report Data:', data)
    console.log('Visible Stats Count:', visibleStats)

    // Safe defaults to prevent undefined/null issues
    const safeData = {
        goldEarned: data?.goldEarned || 0,
        xpGained: data?.xpGained || 0,
        monstersDefeated: data?.monstersDefeated || 0,
        gemsSpent: data?.gemsSpent || 0,
        euroSpent: data?.euroSpent || 0
    }

    const stats = [
        { icon: '🪙', label: 'Einkommen', value: `+${safeData.goldEarned.toLocaleString()}`, type: 'positive' },
        { icon: '✨', label: 'Fortschritt', value: `+${safeData.xpGained.toLocaleString()} XP`, type: 'positive' },
        { icon: '⚔️', label: 'Besiegte Monster', value: safeData.monstersDefeated.toString(), type: 'neutral' },
        { icon: '💎', label: 'Diamanten ausgegeben', value: safeData.gemsSpent.toLocaleString(), type: 'neutral' },
        { icon: '💸', label: 'Echtgeld investiert', value: `${safeData.euroSpent.toFixed(2)}€`, type: 'warning' },
    ]

    // Add debt stat only if > 0
    if (debt > 0) {
        stats.push({ icon: '🏦', label: 'Schulden', value: `${debt.toFixed(2)}€`, type: 'danger' })
    }

    console.log('Stats array:', stats)

    return (
        <div className="daily-report-overlay" onClick={onClose}>
            <div className="daily-report-modal" onClick={(e) => e.stopPropagation()}>
                <div className="daily-report__header">
                    <h2 className="daily-report__title">Tag {dayNumber}</h2>
                    <p className="daily-report__subtitle">Tagesabrechnung</p>
                </div>

                <div className="daily-report__stats">
                    {stats.map((stat, index) => (
                        <div
                            key={index}
                            className={`daily-report__stat-row daily-report__stat--${stat.type} ${visibleStats > index ? 'daily-report__stat--visible' : ''}`}
                            style={{
                                opacity: visibleStats > index ? 1 : 0,
                                transform: visibleStats > index ? 'translateX(0)' : 'translateX(-20px)',
                                transition: 'opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                            }}
                        >
                            <span className="daily-report__stat-icon">{stat.icon}</span>
                            <span className="daily-report__stat-label">{stat.label}</span>
                            <span className="daily-report__stat-value">
                                {stat.value}
                            </span>
                        </div>
                    ))}
                </div>

                {visibleStats >= stats.length && (
                    <div className="daily-report__footer">
                        <button className="daily-report__btn" onClick={onClose}>
                            Weiter
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
